import { computed, readonly, type MaybeRefOrGetter, type Ref } from 'vue';
import type { ApiCandidateDetailCampaign } from '~/composables/useCandidate';
import { useCandidateDetail } from '~/composables/useCandidate';
import type { Campaign } from '~/types/Candidates';
import type { ApiError } from '~/types/plugins/api';

function mapCampaign(raw: ApiCandidateDetailCampaign): Campaign {
  return {
    id: raw.slug,
    title: raw.title,
    type: raw.type,
    description: raw.description ?? '',
    candidateIds: [],
    tags: [],
    // Not present on the nested candidate-detail campaign shape.
    sources: [],
    // A campaign the candidate inherits from their party is party-led; one
    // linked to the candidate directly is candidate-led. `type` can't answer
    // this — it classifies the campaign, not who leads it here.
    leadSource: raw.association_source === 'inherited' ? 'party' : 'candidate',
  };
}

// VTW-432. The Show Candidate response concatenates the candidate's own
// campaigns with those inherited from their party, but that order is incidental
// — the API applies no sort of its own. The profile has to lead with the
// campaigns the candidate runs themselves, so the contract is enforced here.
// A partition rather than a sort, to keep each group's relative order intact.
function orderByLeadSource(campaigns: Campaign[]): Campaign[] {
  return [
    ...campaigns.filter((c) => c.leadSource !== 'party'),
    ...campaigns.filter((c) => c.leadSource === 'party'),
  ];
}

// No dedicated `/candidates/{id}/campaigns` endpoint exists — the candidate's
// campaigns are only available via the Show Candidate response, so this reads
// them off `useCandidateDetail`, which is the single fetch shared with
// useCandidate/useCandidateLobbyists for the same id (see the dedup note on
// `useCandidateDetail` in useCandidate.ts).
export function useCandidateCampaigns(id: MaybeRefOrGetter<string>) {
  const { data, error, status } = useCandidateDetail(id);

  const campaigns = computed(() =>
    orderByLeadSource(data.value?.campaigns?.map(mapCampaign) ?? []),
  );

  return {
    campaigns: readonly(campaigns) as Readonly<Ref<Campaign[]>>,
    pending: computed(() => status.value === 'pending'),
    error: readonly(error) as Readonly<Ref<ApiError | null>>,
  };
}
